import http from "@http";
import config from "@config";
import actionCreators from "@modules/client/actionCreator";
import contextActionCreators from "@modules/context/actionCreator";
import { getClientFilters, getClientsSort } from "@modules/client/selectors";
import {
  AddClientDetails,
  GetAdminSchema,
  GetClients,
  GetClientsCsv,
  GetClientsCsvSuccess,
  SetClientsSort,
  UpdateClientDetails,
  GetAllClientsList,
} from "@modules/client/constants/actions";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import { downloadCsvFile } from "@sagas/utils";
import { all, call, put, takeEvery, select, take, spawn } from "redux-saga/effects";
import { transformFilters } from "@transformers/request";
import { sortToString } from "@transformers/sort";

import { getSchema } from "@utils/schema";

import clientSagas from "./client";
import clientSlaSagas from "./sla";
import clientServiceSagas from "./service-scope";

export function* onGetClients({ payload }) {
  const filters = yield select(getClientFilters);
  const sort = yield select(getClientsSort);
  const additionalFilters = (payload || {}).filters || {};
  const page = (payload || {}).page || 1;

  try {
    const response = yield call(http.get, `api/v1/client-admin/client/statistics`, {
      params: {
        search: transformFilters(Object.assign({}, filters, additionalFilters)),
        page,
        sort: sortToString(sort),
        limit: config.results100PerPage,
      },
    });

    yield put(actionCreators.getClientsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.getClientsError(e.response));
  }
}

export function* onGetClientsCsv() {
  const filters = yield select(getClientFilters);
  try {
    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/client-admin/client/statistics/export`,
      {
        params: {
          search: transformFilters(filters),
        },
        headers: {
          Accept: "text/csv",
        },
      },
    );

    yield put(actionCreators.getClientsCsvSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.getClientsCsvError(e.response));
  }
}

export function* onGetClientsCsvSuccess({ payload }) {
  yield downloadCsvFile(payload, "clients.csv");
}

export function* onGetAdminsSchema() {
  try {
    const schema = getSchema("ClientUserDTO");
    yield put(actionCreators.setAdminSchema(schema));
  } catch (e) {
    yield put(actionCreators.getAdminsError(e.response));
  }
}

export function* onUpdateClientDetails({ payload }) {
  try {
    const clientId = payload.id || null;

    const response = yield call(
      http.put,
      `${config.endpointBaseUrl}api/v1/client-admin/client/${clientId}`,
      payload,
    );
    yield all([
      put(actionCreators.UpdateClientDetailsSuccess(response.data)),
      put(actionCreators.getClient({ clientId })),
      put(contextActionCreators.GetClientContext({ clientId })),
      put(sendSuccessMessage("CLIENT_UPDATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateClientDetailsError(e.response));
  }
}

export function* onAddClientDetails({ payload }) {
  try {
    yield call(http.post, `${config.endpointBaseUrl}api/v1/client-admin/client`, payload);
    yield all([
      put(actionCreators.GetClients(payload)),
      put(actionCreators.AddClientDetailsSuccess()),
      put(actionCreators.ToggleAddClientModal()),
      put(sendSuccessMessage("CLIENT_CREATED")),
    ]);
  } catch (e) {
    yield all([
      put(actionCreators.AddClientDetailsError(e.response)),
      put(actionCreators.ToggleAddClientModal()),
    ]);
  }
}

function* onGetAllClientsList(): Generator<*, void, *> {
  try {
    const resp = yield call(http.get, `${config.endpointBaseUrl}api/v1/client-admin/client`);
    yield put(actionCreators.GetAllClientsListSuccess(resp.data));
  } catch (e) {
    yield put(actionCreators.GetAllClientsListError(e.response));
  }
}

export function* ClientSaga() {
  yield all([
    // Client List
    takeEvery(AddClientDetails, onAddClientDetails),
    takeEvery(GetClients, onGetClients),
    takeEvery(GetClientsCsv, onGetClientsCsv),
    takeEvery(GetClientsCsvSuccess, onGetClientsCsvSuccess),
    takeEvery(SetClientsSort, onGetClients),
    // Client Services
    spawn(clientServiceSagas),
    // Client Details
    takeEvery(UpdateClientDetails, onUpdateClientDetails),
    // Client Admin
    takeEvery(GetAdminSchema, onGetAdminsSchema),
    // SLA
    spawn(clientSlaSagas),
    takeEvery(GetAllClientsList, onGetAllClientsList),
    // Client
    spawn(clientSagas),
  ]);
}

export default ClientSaga;
